import { useState, useContext, useEffect, JSX } from "react";

import { TutorialsService } from "@app/tutorials";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import MegaMenuHeader from "./MegaMenuHeader";
import MegaMenuLink from "./MegaMenuLink";
import MegaMenuStandardLink from "./MegaMenuStandardLink";

import MenuItemEnum from "@app/layout/menus/menuItemEnum";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { FlyoutLink } from "@app/shared/Flyouts";
import AIChatPanel from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPanel";
import ChatbotService from "@app/projects/network-assessment/provider-hub/ai-assistant/chatbotService";
import { AiChatMessage } from "@app/projects/network-assessment/provider-hub/interfaces";

const HelpMenu = (): JSX.Element => {
  const supportService = new TutorialsService();
  const [uri, setUri] = useState<string>("");
  const [title, setTitle] = useState<string>("Loading...")

  const userContext = useContext(UserContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const chatbotService = new ChatbotService();

  const handleSubmitMessage = async (messages: AiChatMessage[]) => {
    const response = await chatbotService.sendUserguideMessage(messages.at(messages.length - 1));
    return [...messages, response];
  }

  const handleSubmitSemanticKernel = async (messages: AiChatMessage[]) => {
    return await chatbotService.sendSemanticKernalConversation(messages);
  }

  // Currently we are just grabbing the first file in the list. At the moment there is only one QES tutorial. This may change, and may need to be handled.
  useEffect(() => {
    if (userContext) {
      supportService.showBlobSas("qes-user-guides")
        .then((qesGuidesStr) => {
          const qesGuides = JSON.parse(qesGuidesStr);
          setTitle(qesGuides[0]?.title);
          setUri(qesGuides[0]?.link);
        });
    }
  }, []);

  return (
    <ul className="menu">
      <li>
        <MegaMenuHeader title="Help"/>
        <ul className="menu">
          <MegaMenuStandardLink faIcon="phone" to={`https://questanalytics.com/contact/`} linkText="Contact Us"
                                menuItem={MenuItemEnum.ContactUs}/>
          <RoleRestricted allowedRoles={[Roles.VIEWER, Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
            <MegaMenuStandardLink faIcon="book" to={uri} linkText={title} target="_blank"
                                  menuItem={MenuItemEnum.QesUserGuide}/>
            {featureFlagContext.enabledFeatureFlags.has(Features.AZURE_AI_POC) && (
              <>
                <FlyoutLink
                  content={{
                    body: <AIChatPanel sendMessages={handleSubmitMessage}
                                       startMessage="What questions can I answer about the QES user guide?"/>,
                    title: 'Userguide Chat',
                  }}
                  testId="help-menu__ai-chat-flyout-link"
                  size="40"
                  classNames="d-flex py-1">
                  <i className="fa fa-comment p-1" style={{ "color": "#32323C" }}></i>
                  <p className="p-1"
                     style={{
                       "color": "#32323C",
                       "fontSize": "0.8rem",
                       "fontWeight": "400",
                       "marginBottom": "0",
                     }}>Userguide Chat</p>
                </FlyoutLink>
                <FlyoutLink
                  content={{
                    body: <AIChatPanel sendMessages={handleSubmitSemanticKernel}
                                       startMessage="How can I help you get things done inside QES?"/>,
                    title: 'AI Assistant',
                  }}
                  testId="help-menu__ai-chat-flyout-link"
                  size="40"
                  classNames="d-flex py-1">
                  <i className="fa fa-flask p-1" style={{ "color": "#32323C" }}></i>
                  <p className="p-1"
                     style={{
                       "color": "#32323C",
                       "fontSize": "0.8rem",
                       "fontWeight": "400",
                       "marginBottom": "0",
                     }}>AI Assistant</p>
                </FlyoutLink>
              </>
            )}
          </RoleRestricted>
          <MegaMenuLink faIcon="file-pdf-o" menuItem={MenuItemEnum.QESTutorial} to="/tutorials/qes"
                        linkText="QES Tutorials"/>
          <RoleRestricted allowedRoles={[Roles.SOFTWARE_LICENSE]}>
            <MegaMenuLink faIcon="file-pdf-o" menuItem={MenuItemEnum.DesktopTutorial} to="/tutorials/qas"
                          linkText="QAS Tutorials"/>
          </RoleRestricted>
        </ul>
      </li>
    </ul>
  );
};

export default HelpMenu;
